/**
 * Serviço de Notificações para PWA
 * Gerencia notificações locais e push para o aplicativo Prumo
 */

export interface NotificationOptions {
  title: string;
  body: string;
  icon?: string;
  badge?: string;
  tag?: string;
  data?: any;
  requireInteraction?: boolean;
  actions?: NotificationAction[];
}

class NotificationService {
  private permission: NotificationPermission = 'default';
  private isEnabled: boolean = true;

  constructor() {
    // Verificar se notificações estão habilitadas no localStorage
    const saved = localStorage.getItem('notifications_enabled');
    if (saved !== null) {
      this.isEnabled = saved === 'true';
    }

    // Verificar permissão atual
    if ('Notification' in window) {
      this.permission = Notification.permission;
    }
  }

  /**
   * Verifica se notificações são suportadas
   */
  isSupported(): boolean {
    return 'Notification' in window && 'serviceWorker' in navigator;
  }

  /**
   * Verifica se notificações estão habilitadas pelo usuário
   */
  isEnabledByUser(): boolean {
    return this.isEnabled;
  }

  /**
   * Habilita ou desabilita notificações
   */
  setEnabled(enabled: boolean): void {
    this.isEnabled = enabled;
    localStorage.setItem('notifications_enabled', enabled.toString());
  }

  /**
   * Solicita permissão de notificações
   */
  async requestPermission(): Promise<NotificationPermission> {
    if (!this.isSupported()) {
      throw new Error('Notificações não são suportadas neste navegador');
    }

    if (this.permission === 'granted') {
      return 'granted';
    }

    try {
      const permission = await Notification.requestPermission();
      this.permission = permission;
      return permission;
    } catch (error) {
      console.error('Erro ao solicitar permissão de notificações:', error);
      throw error;
    }
  }

  /**
   * Obtém a permissão atual
   */
  getPermission(): NotificationPermission {
    if ('Notification' in window) {
      this.permission = Notification.permission;
    }
    return this.permission;
  }

  /**
   * Verifica se tem permissão para enviar notificações
   */
  hasPermission(): boolean {
    return this.getPermission() === 'granted' && this.isEnabled;
  }

  /**
   * Envia uma notificação local
   */
  async showNotification(options: NotificationOptions): Promise<void> {
    if (!this.hasPermission()) {
      console.warn('Notificações não têm permissão ou estão desabilitadas');
      return;
    }

    // Se o Service Worker está disponível, usar ele para notificações mais robustas
    if ('serviceWorker' in navigator) {
      try {
        const registration = await navigator.serviceWorker.ready;
        await registration.showNotification(options.title, {
          body: options.body,
          icon: options.icon || '/icon-192.png',
          badge: options.badge || '/icon-96.png',
          tag: options.tag,
          data: options.data,
          requireInteraction: options.requireInteraction || false,
          actions: options.actions,
          vibrate: [200, 100, 200],
          timestamp: Date.now(),
        });
        return;
      } catch (error) {
        console.error('Erro ao enviar notificação via Service Worker:', error);
        // Fallback para notificação direta
      }
    }

    // Fallback: notificação direta (funciona apenas quando app está aberto)
    try {
      const notification = new Notification(options.title, {
        body: options.body,
        icon: options.icon || '/icon-192.png',
        badge: options.badge || '/icon-96.png',
        tag: options.tag,
        data: options.data,
        requireInteraction: options.requireInteraction || false,
      });

      // Auto-fechar após 5 segundos
      setTimeout(() => {
        notification.close();
      }, 5000);
    } catch (error) {
      console.error('Erro ao criar notificação:', error);
    }
  }

  /**
   * Notifica sobre transações que vencem hoje
   */
  async notifyUpcomingDue(transactions: any[]): Promise<void> {
    if (!this.hasPermission() || transactions.length === 0) {
      return;
    }

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);

    const dueToday = transactions.filter((t) => {
      const dueDate = new Date(t.due_date);
      dueDate.setHours(0, 0, 0, 0);
      return dueDate.getTime() === today.getTime();
    });

    const dueTomorrow = transactions.filter((t) => {
      const dueDate = new Date(t.due_date);
      dueDate.setHours(0, 0, 0, 0);
      return dueDate.getTime() === tomorrow.getTime();
    });

    // Notificar sobre vencimentos de hoje
    if (dueToday.length > 0) {
      const total = dueToday.reduce((sum, t) => sum + (t.amount || 0), 0);
      await this.showNotification({
        title: '💰 Vencimentos Hoje',
        body: `${dueToday.length} transação(ões) vence(m) hoje. Total: R$ ${total.toFixed(2)}`,
        tag: 'due-today',
        data: { type: 'due-today', transactions: dueToday },
        requireInteraction: true,
      });
    }

    // Notificar sobre vencimentos de amanhã (apenas uma vez por dia)
    if (dueTomorrow.length > 0) {
      const lastNotification = localStorage.getItem('due-tomorrow-notified');
      const todayStr = today.toDateString();
      
      if (lastNotification !== todayStr) {
        const total = dueTomorrow.reduce((sum, t) => sum + (t.amount || 0), 0);
        await this.showNotification({
          title: '📅 Vencimentos Amanhã',
          body: `${dueTomorrow.length} transação(ões) vence(m) amanhã. Total: R$ ${total.toFixed(2)}`,
          tag: 'due-tomorrow',
          data: { type: 'due-tomorrow', transactions: dueTomorrow },
        });
        localStorage.setItem('due-tomorrow-notified', todayStr);
      }
    }
  }

  /**
   * Notifica sobre sincronização bem-sucedida
   */
  async notifySyncSuccess(count: number): Promise<void> {
    if (!this.hasPermission()) {
      return;
    }

    await this.showNotification({
      title: '✅ Sincronização Concluída',
      body: `${count} item(ns) sincronizado(s) com sucesso`,
      tag: 'sync-success',
      data: { type: 'sync-success', count },
    });
  }

  /**
   * Notifica sobre erro na sincronização
   */
  async notifySyncError(error: string): Promise<void> {
    if (!this.hasPermission()) {
      return;
    }

    await this.showNotification({
      title: '❌ Erro na Sincronização',
      body: error,
      tag: 'sync-error',
      data: { type: 'sync-error', error },
      requireInteraction: true,
    });
  }

  /**
   * Notifica sobre transação marcada como paga
   */
  async notifyTransactionPaid(transaction: any): Promise<void> {
    if (!this.hasPermission()) {
      return;
    }

    await this.showNotification({
      title: '✅ Transação Paga',
      body: `${transaction.description} foi marcada como paga`,
      tag: `transaction-paid-${transaction.id}`,
      data: { type: 'transaction-paid', transaction },
    });
  }
}

// Exportar instância singleton
export const notificationService = new NotificationService();

